import React, {useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {useTheme} from "@mui/material/styles";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import {Checkbox, Input, Slider} from "@mui/material";
import CardContent from "@mui/material/CardContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Collapse from "@mui/material/Collapse";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {ExpandMore as ExpandMoreIcon} from "@mui/icons-material";
import {styled} from '@mui/system';

import HyperParams from "./HyperParams";
import ModelCards from "./ModelCards";
import {fetchData, postData} from "../../utils/api";
import {UserContext} from "../../App";


// eslint-disable-next-line react/display-name
const Alert = React.forwardRef((props, ref) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

export default function Trainer() {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();
    // needed for routing to pipeline site
    const navigate = useNavigate();
    // get data from current dataset + Experiment
    const {
        datasets,
        currentDatasetIdx,
        experiments,
        currentExperimentIdx,
        currentExperimentProgress,
    } = React.useContext(UserContext);

    const [selectedRun, setSelectedRun] = React.useState(t("trainer.DropdownRunsNew"));
    const [runsNames, setRunsNames] = React.useState([]);
    const [hpOptimizers, setHpOptimizers] = React.useState([]);
    const [dataBalancers, setDataBalancers] = React.useState([]);
    const [dataBalancerOptions, setDataBalancerOptions] = React.useState([]);
    const [selectedOptimizer, setSelectedOptimizer] = React.useState("");
    const [selectedBalancer, setSelectedBalancer] = React.useState("");
    const [selectedBalancerOptions, setSelectedBalancerOptions] = React.useState("");
    const [isNewRun, setIsNewRun] = React.useState(true);
    const [pipelineSteps, setPipelineSteps] = React.useState({});
    const [selectedPipelineSteps, setSelectedPipelineSteps] = React.useState({});
    const handleOptiChange = (event) => {
        setSelectedOptimizer(event.target.value);
    };
    const handleBalancerChange = (event) => {
        setSelectedBalancer(event.target.value);
        setSelectedBalancerOptions(dataBalancerOptions[event.target.value])
    };
    const [pipelineRunName, setPipelineRunName] = React.useState("");
    const handleRunNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPipelineRunName(event.target.value);
    };
    const [numTrials, setNumTrials] = React.useState(100);
    const [selectedModel, setSelectedModel] = React.useState("");
    const [modelHyperParams, setModelHyperParams] = React.useState({});

    const fetchTrainConfig = (currentRunName = t("trainer.DropdownRunsNew")) => {
        // if an existing run is selected, add query parameter to fetch the config of the selected run
        let queryParam = "";
        if (currentRunName !== t("trainer.DropdownRunsNew")) {
            setIsNewRun(false);
            queryParam = `?run_name=${currentRunName}`;
        } else {
            setIsNewRun(true);
        }
        fetchData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run-config${queryParam}`
        ).then((data) => {
            // set datasets and use first dataset as default
            setPipelineSteps(data.availableAdditionalPipelineSteps)
            setSelectedPipelineSteps({})
            setHpOptimizers(data.availableHpOptimizers)
            setDataBalancers(data.availableDataBalancer)
            setDataBalancerOptions(data.availableDataBalancerOptions)
            setSelectedOptimizer(data.availableHpOptimizers[0])
            setSelectedBalancer(data.availableDataBalancer[0])
            setSelectedBalancerOptions(data.availableDataBalancerOptions.None)
            setPipelineRunName(data.runName)
            setNumTrials(data.numTrials)
            setModelHyperParams(data.modelHyperParams)
            setSelectedModel(data.modelType)
        });
    };
    const fetchRuns = () => {
        fetchData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/extendable_runs`
        ).then((data) => {
            // unwrap children dictionaries e.g. metrics and params and change key-val order
            const lastChildRunNames = data.runs.reduce((acc, run) => {
                const lastChild = run.children[run.children.length - 1];
                return lastChild ? acc.concat(lastChild.runName) : acc;
            }, []);
            // add "New Run" as first and default option
            setRunsNames([t("trainer.DropdownRunsNew"), ...lastChildRunNames]);
        });
    };
    const handleRunSelectorChange = (event) => {
        setSelectedRun(event.target.value);
        fetchTrainConfig(event.target.value);
    };
    const handleHyperParamsChange = (parentKey, paramKey, newParamSettings) => {
        setModelHyperParams({
            ...modelHyperParams,
            [selectedModel]: {
                ...modelHyperParams[selectedModel],
                [parentKey]: {
                    ...modelHyperParams[selectedModel][parentKey],
                    [paramKey]: newParamSettings
                },
            }
        });
    };
    const handlePipelineStepHyperParamsChange = (step, paramKey, newParamSettings) => {
        // overwrite pipeline step hyperparams
        setSelectedPipelineSteps({
            ...selectedPipelineSteps,
            [step]: {
                ...selectedPipelineSteps[step],
                "options": {
                    ...selectedPipelineSteps[step].options,
                    [paramKey]: newParamSettings
                },
            }
        });
    }

    const handleDataBalancerOptionsChange = (_, paramKey, newParamSettings) => {
        setSelectedBalancerOptions((prevOptions) => ({
            ...prevOptions,
            [paramKey]: newParamSettings,
        }));
    }

    // fetch Config Data by API requests, when the current dataset is gathered
    useEffect(() => {
        if (datasets.length >= 1) {
            fetchTrainConfig();
            fetchRuns()
        }
    }, [experiments, currentExperimentIdx]);

    const handleModelChange = (modelType) => () => {
        setSelectedModel(modelType);
    };

    const handlePipelineStepSelection = (stepName) => () => {
        // make full copy of selectedPipelineSteps
        const newSelectedPipelineSteps = {...selectedPipelineSteps};
        if (stepName in newSelectedPipelineSteps) {
            delete newSelectedPipelineSteps[stepName];
            if (stepName === "HPO") {
                setSelectedModel("")
            }
        } else {
            newSelectedPipelineSteps[stepName] = {...pipelineSteps[stepName]};
            // remove not needed key
            delete newSelectedPipelineSteps[stepName].allowedModels;
            if (stepName === "HPO") {
                setSelectedModel(Object.keys(modelHyperParams)[0])
            }
        }
        setSelectedPipelineSteps(newSelectedPipelineSteps);
    }

    const handleNumTrialsInputChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setNumTrials(
            event.target.value === "" ? "" : Number(event.target.value)
        );
    };

    const handleBlur = (event) => {
        const newSteps = event.target.valueAsNumber;
        if (newSteps < 0) {
            setNumTrials(0);
        } else if (newSteps > 1000) {
            setNumTrials(1000);
        }
    };


    /* UI-Specific code to implement "ReadMore" behaviour */
    const [expanded, setExpanded] = React.useState(false);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };
    const ExpandMore = styled((props) => {
        const {expand, ...other} = props;
        return <IconButton {...other} />;
    })(({expand}) => ({
        transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
        marginLeft: "auto",
        transition: theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
        }),
    }));

    // Error Snackbars
    const [snackbarSettings, setSnackbarSettings] = React.useState({
        isOpen: false,
        type: "success",
        message: "",
    });

    const handleStartPipeline = () => {
        // set snackbar notification, dependent on post status
        const dictCopy = {...snackbarSettings};

        // construct pipeline steps list
        const allPipelineSteps = []
        let numOfHPOSteps = 0

        if (isNewRun) {
            // every new Run has the pipline step HPO
            numOfHPOSteps = 1
            allPipelineSteps.push({
                "stepName": `HPO01`,
                "modelType": selectedModel,
                "numTrials": Number(numTrials),
                "options": modelHyperParams[selectedModel]
            })

            // then push additional pipeline steps
            Object.entries(selectedPipelineSteps).forEach(([stepName, step]) => {
                allPipelineSteps.push({
                    "stepName": `${stepName}${(isNewRun ? 1 : 2).toString().padStart(2, '0')}`,
                    "options": {[stepName]: step.options},
                    "modelType": selectedModel,
                });
            });
        }
        else {
            // check if HPO is selected
            numOfHPOSteps = Object.keys(selectedPipelineSteps).includes("HPO") ? 1 : 0;
            // add optional additional pipeline steps
            Object.entries(selectedPipelineSteps).forEach(([stepName, step]) => {
                allPipelineSteps.push({
                    "stepName": `${stepName}${(isNewRun? 1: 2).toString().padStart(2, '0')}`,
                    "options": {[stepName]: step.options},
                    "modelType": selectedModel,
                });
            });
        }

        // add additional HPO step, if AFS is in pipelineSteps
        // this HPO uses the same options as the last HPO step
        if (Object.keys(selectedPipelineSteps).includes("AFS")) {
            const HPONumber = numOfHPOSteps + 1;
            allPipelineSteps.push({
                "stepName": `HPO${HPONumber.toString().padStart(2, '0')}`,
                "modelType": selectedModel,
                "numTrials": Number(numTrials),
                "options": modelHyperParams[selectedModel]
            })
        }

        let parentRunName = pipelineRunName
        if (!isNewRun) {
            parentRunName = pipelineRunName.substring(0, pipelineRunName.lastIndexOf("_"));
        }

        // add data selected Data Balancer into each step
        allPipelineSteps.forEach((step) => {
            step.dataBalancer = selectedBalancer;
            step.dataBalancerOptions = selectedBalancerOptions;
        });
        // then start processing: first rigger processing start via API
        postData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run`,
            {
                runName: parentRunName,
                isNewRun,
                hpOptimizer: selectedOptimizer,
                modelType: selectedModel,
                pipelineSteps: allPipelineSteps
            }
        ).then((data) => {
            dictCopy.isOpen = true;
            dictCopy.message = data.data;
            if (data.status === 200) {
                dictCopy.type = "success";
                dictCopy.message = t("trainer.SuccessPipelineStarted");
                // if processing was started, change website state to process-view
                navigate(
                    `/train/${datasets[currentDatasetIdx].datasetName}/${experiments[currentExperimentIdx].experimentName}/pipeline/${parentRunName}`
                );
            } else {
                dictCopy.type = "error";
                if (data.status === 409) {
                    dictCopy.message = t("trainer.ErrorRunAlreadyExists");
                }
            }
            setSnackbarSettings(dictCopy);
        });
    };


    // styles
    const stepCardStyles = {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        color: "black",
        justifyContent: "space-between",
        border: 0,
        borderRadius: "20px",
        backgroundColor: theme.palette.background.default,
        paddingBottom: `${theme.spacing(2)}`,
        transition: "background-color 200ms, box-shadow 200ms",
        "& .MuiTypography-body1": {
            color: "black",
        },
        "& .MuiSvgIcon-root": {
            color: theme.palette.primary.main
        },
        "&:hover, &.selected": {
            backgroundColor: theme.palette.primary.light,
            border: 0,
            boxShadow: "2px 2px 5px #00000057",
            transition: "background-color 200ms, box-shadow 200ms",
            "& .MuiTypography-h5": {
                color: theme.palette.primary.dark,
                transition: "color 300ms",
            },
        },
    };


    return (
        <Grid
            id="trainer-container"
            container
        >
            <Grid
                container
                sx={{
                    padding: theme.spacing(8, 8),
                    borderRadius: '16px',
                    backgroundColor: 'white',
                }}
            >
                {
                    // only render if current experiment has fulfilled all required steps and stats are loaded
                    currentExperimentProgress?.processed ? (
                        <>
                            <Snackbar open={snackbarSettings.isOpen} autoHideDuration={8000}>
                                <Alert severity={snackbarSettings.type}>
                                    {snackbarSettings.message}
                                </Alert>
                            </Snackbar>

                            <Grid item xl={12} lg={12} md={12} xs={12}>
                                <Grid container spacing={2} rowSpacing={4}>
                                    <Grid item lg={12} xs={12}>
                                        <Typography variant="h4"
                                                    gutterBottom>{t("trainer.TitleRunCreation")}</Typography>
                                    </Grid>
                                    <Grid item lg={3} xs={6}>
                                        <FormControl>
                                            <InputLabel>{t("trainer.DropdownRunsLabel")}</InputLabel>
                                            <Select
                                                displayEmpty
                                                value={selectedRun}
                                                label={t("trainer.DropdownRunsLabel")}
                                                onChange={handleRunSelectorChange}
                                                sx={{minWidth: 200}}
                                                renderValue={(selected) => {
                                                    if (isNewRun) {
                                                        return <em>{t("trainer.DropdownRunsNew")}</em>;
                                                    }
                                                    return selected
                                                }}
                                            >
                                                {runsNames.map((name, index) => (
                                                    <MenuItem key={`run-selection-${index}`} value={name}>
                                                        {name}
                                                    </MenuItem>
                                                ))}
                                            </Select>

                                        </FormControl>
                                        <Tooltip title={t("trainer.HintDropdownRuns")}>
                                            <IconButton>
                                                <InfoIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item lg={9} xs={6}>
                                        <FormControl>
                                            <TextField
                                                label={t("trainer.InputRunName")}
                                                value={`${pipelineRunName}`}
                                                onChange={handleRunNameChange}
                                                disabled={!isNewRun}
                                            />
                                        </FormControl>
                                        <Tooltip title={t("trainer.HintRunName")}>
                                            <IconButton>
                                                <InfoIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item lg={3} xs={6}>
                                        <FormControl>
                                            <InputLabel>
                                                {t("trainer.DropdownBalancerMethod")}
                                            </InputLabel>
                                            <Select
                                                value={selectedBalancer}
                                                label={t("trainer.DropdownBalancerMethod")}
                                                onChange={handleBalancerChange}
                                                sx={{minWidth: 200}}
                                            >
                                                {dataBalancers.map((name) => (
                                                    <MenuItem key={name} value={name}>
                                                        {name === "None" ?
                                                            <i>{t('trainer.SelectedNoDataBalancer')}</i> : name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Tooltip title={t("trainer.HintBalancerMethod")}>
                                            <IconButton>
                                                <InfoIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>

                                    {isNewRun && (
                                        <>
                                            <Grid item lg={12} xs={12}>
                                                <Typography variant="h4"
                                                            gutterBottom>{t("trainer.TitleTrainingConfiguration")}</Typography>
                                            </Grid>
                                            <ModelCards modelHyperParams={modelHyperParams}
                                                        handleModelChange={handleModelChange}
                                                        selectedModel={selectedModel} isOptional={false}/>
                                        </>
                                    )}
                                </Grid>

                                {!isNewRun && (
                                    <Divider textAlign="left" sx={{margin: theme.spacing(2)}}>
                                        {t("trainer.TitlePipelineSteps")}
                                        <Tooltip title={t("trainer.HintPipelineSteps")}>
                                            <IconButton>
                                                <InfoIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </Divider>
                                )}
                                <Grid container spacing={2} sx={{"marginTop": theme.spacing(2)}}>
                                    {!isNewRun && (
                                        <>
                                            <Grid item lg={12} md={12} xs={12}
                                                  sx={{display: "flex", alignItems: "stretch", pointerEvents: "none", opacity: 0.2}}
                                            >
                                                <Card sx={stepCardStyles}>
                                                    <CardContent>
                                                        <FormControlLabel
                                                            sx={{
                                                                "margin": 0,
                                                                "width": "30px",
                                                                "transform": "translateX(-10px)"
                                                            }}
                                                            control={
                                                                <Checkbox
                                                                    checked={Object.keys(selectedPipelineSteps).includes("HPO")}
                                                                    onChange={handlePipelineStepSelection("HPO")}
                                                                />
                                                            }
                                                        />
                                                        <Typography variant="h6" gutterBottom
                                                                    sx={{"display": "inline"}}>
                                                            {t(`trainer.TitleAdditionalHPO`)}
                                                        </Typography>

                                                        <Grid container spacing={2} rowSpacing={4}>
                                                            <ModelCards modelHyperParams={modelHyperParams}
                                                                        handleModelChange={handleModelChange}
                                                                        selectedModel={selectedModel} isOptional
                                                                        disabled={!Object.keys(selectedPipelineSteps).includes("HPO")}/>
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </>
                                    )}
                                    {(isNewRun || (Object.keys(selectedPipelineSteps).includes("HPO"))) && (
                                        <>
                                            <Grid item lg={3} xs={6}>
                                                <InputLabel>
                                                    {t('trainer.SliderNumTrials')}
                                                    <Tooltip title={t('trainer.HintNumTrials')}>
                                                        <IconButton><InfoIcon/></IconButton>
                                                    </Tooltip>
                                                </InputLabel>
                                                <Slider
                                                    aria-label="Number of Trials"
                                                    value={typeof numTrials === 'number' ? numTrials : 0}
                                                    onChange={handleNumTrialsInputChange}
                                                    valueLabelDisplay="auto"
                                                    step={5}
                                                    marks
                                                    min={10}
                                                    max={1000}
                                                />
                                            </Grid>

                                            <Grid item lg={2} xs={4} sx={{margin: theme.spacing(5, 0, 0, 3)}}>
                                                <Input
                                                    value={numTrials}
                                                    size="small"
                                                    onChange={handleNumTrialsInputChange}
                                                    onBlur={handleBlur}
                                                    inputProps={{
                                                        step: 5,
                                                        min: 4,
                                                        max: 1000,
                                                        type: 'number',
                                                        'aria-labelledby': 'input-slider',
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item lg={6} xs={6} sx={{"marginTop": theme.spacing(2)}}>
                                                <FormControl>
                                                    <InputLabel>
                                                        {t("trainer.DropdownOptiMethod")}
                                                    </InputLabel>
                                                    <Select
                                                        value={selectedOptimizer}
                                                        label={t("trainer.DropdownOptiMethod")}
                                                        onChange={handleOptiChange}
                                                        sx={{minWidth: 200}}
                                                    >
                                                        {hpOptimizers.map((name) => (
                                                            <MenuItem key={name} value={name}>
                                                                {name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <Tooltip title={t("trainer.HintOptiMethod")}>
                                                    <IconButton>
                                                        <InfoIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>

                                {Object.keys(pipelineSteps).some((step) =>
                                    ((pipelineSteps[step].allowedModels.includes(selectedModel)) || (selectedModel === ""))
                                ) && (
                                    // only render this section if current model has any available pipeline steps
                                    <Grid container spacing={2} sx={{"marginTop": theme.spacing(2)}}>
                                        <Grid item xs={12}>
                                            <Divider textAlign="left">
                                                {t("trainer.TitlePipelineSteps")}
                                                <Tooltip title={t("trainer.HintPipelineSteps")}>
                                                    <IconButton>
                                                        <InfoIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Divider>
                                        </Grid>

                                        {Object.keys(pipelineSteps).map((step) => (
                                            ((pipelineSteps[step].allowedModels.includes(selectedModel)) || (selectedModel === "")) &&  // Only render steps that are allowed for the selected model
                                            (
                                                <Grid item lg={4} md={4} xs={12}
                                                      sx={{display: "flex", alignItems: "stretch"}}
                                                      key={t(`trainer.Title${step.replace(/\s/g, '')}`)}>
                                                    <Card sx={stepCardStyles}>
                                                        <CardContent>
                                                            <FormControlLabel
                                                                sx={{
                                                                    "margin": 0,
                                                                    "width": "30px",
                                                                    "transform": "translateX(-10px)"
                                                                }}
                                                                control={
                                                                    <Checkbox
                                                                        checked={Object.keys(selectedPipelineSteps).includes(step)}
                                                                        onChange={handlePipelineStepSelection(step)}
                                                                    />
                                                                }
                                                            />
                                                            <Typography variant="h6" gutterBottom
                                                                        sx={{"display": "inline"}}>
                                                                {t(`trainer.Title${step.replace(/\s/g, '')}`)}
                                                            </Typography>
                                                            <Typography color="text.secondary">
                                                                {t(`trainer.Description${step.replace(/\s/g, '')}`)}
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            )
                                        ))}
                                        <Grid item lg={12} md={12} xs={12}>
                                            <Typography
                                                variant="body1"
                                                style={{"fontStyle": "italic", "color": theme.palette.primary.main}}
                                                gutterBottom
                                            >
                                                {t(`trainer.HintCanBeExecutedLater`)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}

                                <Grid container spacing={2} sx={{"marginTop": theme.spacing(2)}}>
                                    <Grid item xs={12}>
                                        <InputLabel sx={{
                                            textAlign: "left",
                                        }}>{t('trainer.AdvancedOptions')}
                                            <ExpandMore
                                                expand={expanded}
                                                onClick={handleExpandClick}
                                                aria-expanded={expanded}
                                                aria-label="Advanced Options"
                                            >
                                                <ExpandMoreIcon/>
                                            </ExpandMore>
                                        </InputLabel>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                                            {selectedModel !== "" && (
                                                <>
                                                    <Typography component="h4" gutterBottom>
                                                        {t("trainer.OptimizationConfigTitle")}
                                                        <Tooltip title={t('trainer.HintOptimizationConfig')}>
                                                            <IconButton><InfoIcon/></IconButton>
                                                        </Tooltip>
                                                    </Typography>

                                                    <HyperParams hyperParams={modelHyperParams[selectedModel]}
                                                                 handleChange={handleHyperParamsChange} isPartofHPO/>
                                                </>
                                            )}

                                            {Object.keys(selectedPipelineSteps).map((step, index) => (
                                                <div key={step}>
                                                    <Grid item xs={12}>
                                                        {(index === 0) && (
                                                            <Typography component="h5" gutterBottom>
                                                                <br/>{t("trainer.HyperParamsPiplineStepsTitle")}
                                                                <br/>
                                                            </Typography>
                                                        )
                                                        }
                                                        <HyperParams
                                                            hyperParams={{[step]: selectedPipelineSteps[step].options}}
                                                            handleChange={handlePipelineStepHyperParamsChange}
                                                            isPartofHPO={false}/>
                                                    </Grid>
                                                </div>
                                            ))}
                                            {Object.keys(selectedBalancerOptions || {}).length > 0 && (
                                                <div>
                                                    <Grid item xs={12}>
                                                        <Typography component="h5" gutterBottom>
                                                            <br/>
                                                            {t("trainer.DataBalancingTitle")}
                                                            <br/>
                                                        </Typography>
                                                        <HyperParams
                                                            hyperParams={{[selectedBalancer]: selectedBalancerOptions}}
                                                            handleChange={handleDataBalancerOptionsChange}
                                                            isPartofHPO={false}
                                                        />
                                                    </Grid>
                                                </div>
                                            )}
                                        </Collapse>
                                    </Grid>
                                    <Grid item xs={4} sx={{alignSelf: "end"}}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="medium"
                                            onClick={handleStartPipeline}
                                            disabled={(Object.keys(selectedPipelineSteps).length === 0 && !isNewRun)}
                                        >
                                            {t("trainer.StartButton")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                        // if user lands on this page, without having all required steps fulfilled, show hint}
                        <Grid item xs={12}>
                            <Typography variant="h5" gutterBottom>
                                {t("trainer.TitlePage")}
                            </Typography>
                            <Typography variant="body">
                                {t("hints.NotYetProcessed")}
                            </Typography>
                        </Grid>
                    )}
            </Grid>
        </Grid>
    )
        ;
}
